/**
 * vomFASS — `<vf-variant-chips>` custom element.
 *
 * Registers the chip-style variant picker so it works both on the
 * server-rendered PDP AND inside the quick-add dialog on collection
 * pages. The latter is the reason this is an asset file rather than
 * an inline IIFE: `assets/quick-add.js` pulls the product page via
 * `DOMParser`, then either `morph()`s it into the modal or, on mobile,
 * plucks out the picker + form + price and discards the rest. In
 * neither path do parsed `<script>` tags re-execute, so an inline
 * `<script>` IIFE next to the element silently never runs.
 *
 * Custom elements sidestep all of that — the browser upgrades any
 * `<vf-variant-chips>` it finds in the DOM and runs `connectedCallback`
 * automatically.
 *
 * The element reads everything it needs from data-attributes:
 *   data-product-url    — Shopify product URL (no query string)
 *   data-section-id     — section.id of the source PDP section
 *   data-product-id     — product.id (string compare against
 *                          product-form-component dataset)
 *   data-block-id       — block.id (used as the variant:update sourceId)
 *   data-options-count  — product.options.size
 *   data-variants       — JSON-encoded product.variants array
 *
 * The variant-change flow mirrors Horizon's own variant-picker:
 *   1. Find the new variant from the option selection.
 *   2. Fetch the section HTML at `?variant=NEW_ID&section_id=SECTION_ID`.
 *   3. Dispatch `variant:update` with detail matching `VariantUpdateEvent`
 *      so Horizon's `<product-form-component>` (which listens on the
 *      nearest `.shopify-section, dialog, product-card`) runs its
 *      `#onVariantUpdate` — writes the form's variantId, drains the
 *      ATC queue, morphs in fresh button HTML, toggles accelerated
 *      checkout.
 */

class VfVariantChips extends HTMLElement {
  constructor() {
    super();
    /** @type {AbortController | null} */
    this.inflight = null;
  }

  connectedCallback() {
    /** @type {Array<{id:number, options:Array<string>, available:boolean, price:number}>} */
    this.variants = JSON.parse(this.dataset.variants || '[]');
    this.productUrl   = this.dataset.productUrl   || '';
    this.sectionId    = this.dataset.sectionId    || '';
    this.productId    = String(this.dataset.productId || '');
    this.blockId      = this.dataset.blockId      || '';
    this.optionsCount = parseInt(this.dataset.optionsCount || '1', 10);

    // Wire up chip change handlers. Use the AbortController of the
    // element's own lifecycle so listeners get cleaned up on
    // disconnection (the quick-add modal `morph()`s content in/out
    // repeatedly).
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.querySelectorAll('[data-vf-chip-input]').forEach((input) => {
      input.addEventListener('change', () => this.#onChipChange(input), { signal });
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.inflight?.abort();
    this.inflight = null;
  }

  /**
   * Visual chip swap + variant lookup + apply.
   * @param {HTMLInputElement} input
   */
  #onChipChange(input) {
    // Toggle `.is-selected` on the chips in this radio group.
    this.querySelectorAll(`[name="${input.name}"]`).forEach((sib) => {
      const label = sib.closest('.vf-chips__chip');
      if (label) label.classList.toggle('is-selected', sib.checked);
    });

    const variant = this.#pickVariant();
    if (variant) this.#applyVariant(variant);
  }

  /**
   * Resolve the variant from the currently-selected option values.
   * @returns {object | null}
   */
  #pickVariant() {
    const selected = [];
    for (let i = 0; i < this.optionsCount; i++) {
      const checked = this.querySelector(`input[name^="vf-option-${i}-"]:checked`);
      if (checked) selected[i] = /** @type {HTMLInputElement} */ (checked).value;
    }
    for (const v of this.variants) {
      let match = true;
      for (let j = 0; j < this.optionsCount; j++) {
        if (v.options[j] !== selected[j]) { match = false; break; }
      }
      if (match) return v;
    }
    return null;
  }

  /**
   * Sync URL, fetch section HTML at the new variant, dispatch
   * `variant:update` so Horizon's product-form-component reacts.
   * @param {object} v - The chosen variant
   */
  async #applyVariant(v) {
    this.#syncUrl(v);

    // Cancel any in-flight section fetch — only the latest chip click matters.
    this.inflight?.abort();
    this.inflight = new AbortController();
    const { signal } = this.inflight;

    try {
      const sep = this.productUrl.indexOf('?') === -1 ? '?' : '&';
      const sectionUrl = `${this.productUrl}${sep}variant=${v.id}&section_id=${this.sectionId}`;
      const resp = await fetch(sectionUrl, { signal });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const text = await resp.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');

      // Detail shape matches `VariantUpdateEvent` (assets/events.js). The
      // event bubbles up from this element; Horizon's
      // `<product-form-component>` listens on its nearest
      // `.shopify-section, dialog, product-card` ancestor and catches it.
      const ev = new Event('variant:update', { bubbles: true });
      // @ts-expect-error — adding `detail` to a plain Event is the established pattern in this codebase.
      ev.detail = {
        resource: v,
        sourceId: this.blockId,
        data: {
          html: doc,
          productId: this.productId,
          newProduct: null,
        },
      };
      this.dispatchEvent(ev);
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      console.warn('[vf-variant-chips] variant update failed', err);
      // Fallback so ATC at least submits the right variant if the section
      // fetch failed. Looks up the nearest hidden id input from this
      // element's scope (works in PDP + in the quick-add dialog).
      const scope = this.closest('.product-details, dialog, .shopify-section, body');
      const idInput = scope && scope.querySelector('input[name="id"]');
      if (idInput) /** @type {HTMLInputElement} */ (idInput).value = String(v.id);
    }
  }

  /**
   * Keep `?variant=` in sync with the chip selection so deep-links and
   * page refreshes land on the right size.
   * @param {{id:number}} v
   */
  #syncUrl(v) {
    if (!history || !history.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(v.id));
    history.replaceState({}, '', url.toString());
  }
}

if (!customElements.get('vf-variant-chips')) {
  customElements.define('vf-variant-chips', VfVariantChips);
}
